import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { api_getProductById } from '~/backend/api_calls';
import GuestWishlist from '~/backend/guest_wishlist';
import ShopCart from '~/backend/shop_cart';
import { default as UserSession, default as UserState } from '~/backend/user_session';
import { sendAddToCartFacebookEvent } from '~/common/facebookPixel.js';
import { sendAddToCartGTAG4Event } from '~/common/gtag4.js';
import { sendAddToCartGTMEvent } from '~/common/gtm.js';
import { calculateProductPriceValue } from '~/common/helper.js';
import { sendAddedToCartKlaviyoEvent } from '~/common/klaviyo.js';
import { sendAddToCartTiktokEvent } from '~/common/tiktokPixel.js';
import { notifyInfo } from '~/components/common/ToastMessages';
import { addToCartAction, addToWishlistAction, openCartWidgetAction } from '~/store/actions.js';
import { notifyError } from '../common/ToastMessages';

const usePublicProductActions = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	useEffect(() => {
		const handleAddToCart = event => {
			const { productId, dimension } = event.detail;
			api_getProductById(productId)
				.then(product => {
					const price = calculateProductPriceValue(product, dimension);
					if (!price || price <= 0) {
						notifyError(t('zero_price'));
						return;
					}

					dispatch(
						addToCartAction({
							pid: product.id,
							dimension: dimension,
							quantity: 1,
							product: product
						})
					);

					if (window.innerWidth > 900) {
						dispatch(openCartWidgetAction(true));
						document.body.classList.add('cart-open');

						setTimeout(() => {
							document.body.classList.remove('cart-open');
							dispatch(openCartWidgetAction(false));
						}, 2000);
					}

					notifyInfo(t('added_to_cart'));
					sendAddedToCartKlaviyoEvent(UserState.getEmail(), product, ShopCart.getItems());
					sendAddToCartGTAG4Event(product, dimension, 1);
					sendAddToCartFacebookEvent(product);
					sendAddToCartTiktokEvent(product);
					sendAddToCartGTMEvent(product, dimension, 1);
				})
				.catch(err => {
					console.log('Findbar Product Not Found');
				});
		};

		const handleAddToWishlist = event => {
			const { productId } = event.detail;
			api_getProductById(productId).then(product => {
				if (UserSession.isAuthenticated()) {
					dispatch(
						addToWishlistAction({
							productId: product.id
						})
					);
					document.body.classList.add('overflow');
				} else {
					let status = GuestWishlist.addOrRemove(product);
					let message = '';

					if (status === 'added') {
						message = t('product_added_to_wishlist_guest');
					} else {
						message = t('product_removed_from_wishlist_guest');
					}

					notifyInfo(message);
				}
			});
		};

		const handleGoToSearchResults = event => {
			const { query } = event.detail;
			if (!query) return;
			navigate(`/search-results?query=${query}`);
		};

		const handleClickProduct = event => {
			const { productId } = event.detail;
			api_getProductById(productId).then(product => {
				navigate(product.eurl);
			});
		};

		const handleFindbarRedirect = event => {
			const { url } = event.detail;
			if (!url) return;
			navigate(url);
		};

		document.addEventListener('add-to-cart', handleAddToCart); // PAYLOAD: {productId, dimension}
		document.addEventListener('add-to-wishlist', handleAddToWishlist); // PAYLOAD: {productId}
		document.addEventListener('show-search-results', handleGoToSearchResults); // PAYLOAD: {query}
		document.addEventListener('click-product', handleClickProduct); // PAYLOAD: {productId}
		document.addEventListener('findbar-redirect', handleFindbarRedirect); // PAYLOAD: {url}

		return () => {
			document.removeEventListener('add-to-cart', handleAddToCart);
			document.removeEventListener('add-to-wishlist', handleAddToWishlist);
			document.removeEventListener('show-search-results', handleGoToSearchResults);
			document.removeEventListener('click-product', handleClickProduct);
			document.removeEventListener('findbar-redirect', handleFindbarRedirect);
		};
	}, []);

	return null;
};

export default usePublicProductActions;
